/**
 * CountryDropdown — Reusable ISO 3166-1 country selector.
 *
 * - Searchable list with country flags
 * - USA displayed first, remaining countries alphabetical
 * - Uses `country-data-list` for ISO standard data
 * - Uses `react-circle-flags` for flag icons
 * - Built on shadcn/ui Popover + Command
 *
 * Usage:
 *   <CountryDropdown
 *     value={country}
 *     onChange={(c) => setCountry(c.name)}
 *   />
 */

import * as React from 'react'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { countries } from 'country-data-list'
import { CircleFlag } from 'react-circle-flags'
import { CheckIcon, ChevronDown, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

// ============================================================================
// Types
// ============================================================================

export interface Country {
  alpha2: string
  alpha3: string
  countryCallingCodes: string[]
  currencies: string[]
  emoji?: string
  ioc: string
  languages: string[]
  name: string
  status: string
}

// All valid countries — USA first, then alphabetical
const ALL_COUNTRIES: Country[] = (() => {
  const valid = (countries.all as Country[]).filter(
    (c) => c.emoji && c.status !== 'deleted' && c.ioc !== 'PRK' && c.name,
  )
  const usa = valid.find((c) => c.alpha3 === 'USA')
  const rest = valid.filter((c) => c.alpha3 !== 'USA').sort((a, b) => a.name.localeCompare(b.name))
  return usa ? [usa, ...rest] : rest
})()

// G20 member nations (sovereign states; the EU bloc is not a selectable country).
// US first, then alphabetical — same ordering as ALL_COUNTRIES. Pass this as the
// `options` prop to restrict the dropdown to G20 (e.g. organization address form).
const G20_ALPHA2 = ['US', 'AR', 'AU', 'BR', 'CA', 'CN', 'FR', 'DE', 'IN', 'ID', 'IT', 'JP', 'MX', 'RU', 'SA', 'ZA', 'KR', 'TR', 'GB']
export const G20_COUNTRIES: Country[] = ALL_COUNTRIES.filter((c) => G20_ALPHA2.includes(c.alpha2))

// ============================================================================
// Props
// ============================================================================

export interface CountryDropdownProps {
  /** Currently selected country name */
  value?: string
  /** Called with the full Country object on selection */
  onChange?: (country: Country) => void
  options?: Country[]
  disabled?: boolean
  placeholder?: string
  /** Compact mode — shows flag only */
  slim?: boolean
  /** Dialling-code mode (phone input) — trigger shows flag + code, list shows each country's code */
  dialCode?: boolean
  /** Extra classes for the trigger button (e.g. to embed it inside another control) */
  triggerClassName?: string
  label?: string
  required?: boolean
  error?: string
  className?: string
}

// ============================================================================
// Component
// ============================================================================

const CountryDropdownComponent = (
  {
    value,
    onChange,
    options = ALL_COUNTRIES,
    disabled = false,
    placeholder = 'Select a country',
    slim = false,
    dialCode = false,
    triggerClassName,
    label,
    required = false,
    error,
    className,
    ...props
  }: CountryDropdownProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Country | undefined>(undefined)

  // Sync selected state when value changes
  useEffect(() => {
    if (value) {
      const match = options.find(
        (c) => c.name === value || c.alpha3 === value || c.alpha2 === value,
      )
      setSelected(match)
    } else {
      setSelected(undefined)
    }
  }, [value, options])

  const handleSelect = useCallback(
    (country: Country) => {
      setSelected(country)
      onChange?.(country)
      setOpen(false)
    },
    [onChange],
  )

  // Matches the shared Input / SelectTrigger look (h-10, rounded-lg, border-2, primary focus ring).
  const triggerClasses = cn(
    'flex h-10 w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border-2 border-input bg-background/50 px-4 py-2 text-sm shadow-sm transition-all duration-200',
    'hover:border-primary/30 hover:bg-background/70',
    'focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary',
    'disabled:cursor-not-allowed disabled:opacity-50',
    '[&>span]:line-clamp-1',
    slim && 'w-20',
    error && 'border-destructive',
    triggerClassName,
  )

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          ref={ref}
          className={triggerClasses}
          disabled={disabled}
          type="button"
          {...props}
        >
          {selected ? (
            <div className={cn('flex items-center gap-2 overflow-hidden', !dialCode && 'flex-grow w-0')}>
              <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                <CircleFlag countryCode={selected.alpha2.toLowerCase()} height={20} />
              </div>
              {dialCode ? (
                <span className="tabular-nums">{selected.countryCallingCodes[0]}</span>
              ) : !slim && (
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {selected.name}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">
              {slim ? <Globe size={20} /> : placeholder}
            </span>
          )}
          <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
        </PopoverTrigger>

        <PopoverContent
          collisionPadding={10}
          side="bottom"
          // z-[60] so the popover renders ABOVE a modal Dialog (dialog is z-50). Without
          // this, the country list opens behind the address dialog and looks invisible.
          align="start"
          className={cn('z-[60] min-w-[--radix-popper-anchor-width] p-0', dialCode && 'w-72')}
        >
          <Command className="w-full">
            {/* Search stays fixed ABOVE the scroll region (canonical cmdk layout) — nesting
                it inside CommandList broke the list's scrolling. */}
            <CommandInput placeholder="Search country..." />
            <CommandList
              className="max-h-[200px] sm:max-h-[270px] overflow-y-auto"
              // Inside a modal Dialog, Radix's scroll-lock (react-remove-scroll) swallows
              // wheel events on this portaled popover, so the list won't wheel-scroll.
              // Drive scrollTop manually so the wheel works everywhere (dialog or page).
              onWheel={(e) => {
                e.currentTarget.scrollTop += e.deltaY
              }}
            >
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {options
                  .filter((c) => c.name)
                  .map((option, i) => (
                    <CommandItem
                      key={`${option.alpha3}-${i}`}
                      className="flex items-center w-full gap-2"
                      onSelect={() => handleSelect(option)}
                    >
                      <div className="flex flex-grow w-0 space-x-2 overflow-hidden">
                        <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                          <CircleFlag countryCode={option.alpha2.toLowerCase()} height={20} />
                        </div>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {option.name}
                        </span>
                      </div>
                      {dialCode && (
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {option.countryCallingCodes[0]}
                        </span>
                      )}
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4 shrink-0',
                          option.alpha3 === selected?.alpha3 ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

CountryDropdownComponent.displayName = 'CountryDropdown'

export const CountryDropdown = forwardRef(CountryDropdownComponent)
